'use client';

import { ReactElement } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CartItem as CartItemCard } from './cart-item/cart-item.component';
import { CartLaboratoryGroup } from './cart-laboratory-group/cart-laboratory-group.component';
import { CartProfessionalGroup } from './cart-professional-group/cart-professional-group.component';

import { useChangeCartPatient } from '@/hooks/use-change-cart-patient.hook';
import { useNow } from '@/hooks/use-now.hook';
import { useRemoveFromCart } from '@/hooks/use-remove-from-cart.hook';
import { CartRemovalScope } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { CartItem } from '@/types/cart.types';
import { CartGroupTypeEnum, CartItemGroup, groupCartItems } from '@/utils/cart/group-cart-items.util';
import { formatCartItemCountdownLabel } from '@/utils/cart/format-cart-item-countdown.util';
import { getDuplicatedCartItemIds } from '@/utils/cart/get-duplicated-cart-items.util';

const COUNTDOWN_TICK_MS = 15 * 1000;

function noop() {}

interface CartItemsListProps {
  items: CartItem[];
}

interface RenderGroupHandlers {
  onRemoveItem: (item: CartItem) => void;
  onRemoveItems: (items: CartItem[]) => void;
  onEditItemPatient: (item: CartItem) => void;
  onEditGroupPatient: (items: CartItem[]) => void;
}

function renderGroup(
  group: CartItemGroup,
  handlers: RenderGroupHandlers,
  duplicatedItemIds: ReadonlySet<string>,
  now: Date,
): ReactElement {
  const { onRemoveItem, onRemoveItems, onEditItemPatient, onEditGroupPatient } = handlers;

  switch (group.type) {
    case CartGroupTypeEnum.LABORATORY:
      return (
        <CartLaboratoryGroup
          items={group.items}
          duplicatedItemIds={duplicatedItemIds}
          onEditSchedule={noop}
          onEditPatient={() => onEditGroupPatient(group.items)}
          onRemoveExam={onRemoveItem}
          onRemoveAll={() => onRemoveItems(group.items)}
        />
      );

    case CartGroupTypeEnum.PROFESSIONAL:
      return (
        <CartProfessionalGroup
          items={group.items}
          duplicatedItemIds={duplicatedItemIds}
          now={now}
          onEditSchedule={noop}
          onEditPatient={onEditItemPatient}
          onRemove={onRemoveItem}
        />
      );

    case CartGroupTypeEnum.SINGLE:
      return (
        <CartItemCard
          item={group.items[0]}
          warning={formatCartItemCountdownLabel(group.items[0], now)}
          isDuplicated={duplicatedItemIds.has(group.items[0].id)}
          onEditSchedule={noop}
          onEditPatient={() => onEditItemPatient(group.items[0])}
          onRemove={() => onRemoveItem(group.items[0])}
        />
      );
  }
}

export function CartItemsList({ items }: CartItemsListProps) {
  const { removeItem, removeItems } = useRemoveFromCart();
  const { changeItemPatient, changeGroupPatient } = useChangeCartPatient();

  const now = useNow(COUNTDOWN_TICK_MS);

  const groups = groupCartItems(items);
  const duplicatedItemIds = getDuplicatedCartItemIds(items);

  const handlers: RenderGroupHandlers = {
    onRemoveItem: (item) => void removeItem(item),
    onRemoveItems: (groupItems) => void removeItems(groupItems, CartRemovalScope.GROUP),
    onEditItemPatient: changeItemPatient,
    onEditGroupPatient: changeGroupPatient,
  };

  return (
    <ul
      className="flex flex-col gap-4 px-6 pb-5"
      data-testid={DataTestIdEnum.CART_ITEMS_LIST}
    >
      {groups.map((group) => (
        <li key={group.id}>{renderGroup(group, handlers, duplicatedItemIds, now)}</li>
      ))}
    </ul>
  );
}
